import {KeyValueMap} from 'src/app/models/key-value-map';

export function isFunction(obj): boolean {
  return !!(obj && obj.constructor && obj.call && obj.apply);
}

export function isObject(obj): boolean {
  return (obj && typeof obj === 'object' && !Array.isArray(obj));
}

export function getByPath(obj, path: string, defaultValue?: any): any {
  const result = path.split('.').reduce(
    (acumulator, currentValue) => acumulator ? acumulator[currentValue] : defaultValue, obj);
  if (result == null) {
    return defaultValue;
  }
  return result;
}

export function isDate(date: any): boolean {
  return date instanceof Date && !isNaN(date.valueOf());
}

export function setByPath(obj, path: string, newValue: any): any {
  let currentNode = obj;
  const pathTokens = path.split('.');
  let i = 0;
  for (; i < pathTokens.length - 1; i++) {
    currentNode = currentNode ? currentNode[pathTokens[i]] : null;
  }

  if (currentNode != null) {
    currentNode[pathTokens[i]] = newValue;
  }
}

/**
 * Filters the list based on dynamic key and value
 *
 * @param list - the list to be filtered
 * @param key - the key used for filtering the list
 * @param value - the value that the key should have to be selected by the filter
 * @returns the number of list elements that successfully pass the filter
 */
export function filterAndCount(list, key, value): number {
  return list
    .filter(l => l[key] === value)
    .length;
}

export function toBoolean(value: boolean | string): boolean {
  if (value == null) {
    return false;
  }

  if (typeof value === 'boolean') {
    return value;
  }
  return value.toLowerCase() === 'true';
}

/**
 * Calculates the difference in days between two dates
 *
 * @param firstDate - the first date
 * @param secondDate - the second one
 * @returns the difference in days between those two days
 */
export function getDiffDays(firstDate: Date, secondDate: Date): number {
  const oneDay = 24 * 60 * 60 * 1000;
  const firstDateMs = new Date(firstDate).getTime();
  const secondDateMs = new Date(secondDate).getTime();

  return Math.round((secondDateMs - firstDateMs) / oneDay);
}

/**
 * Flats an object to a single level map.
 *
 * @example
 * // returns {event: 'createInstance', entity.entityId: 123}
 * flatObject({event: 'createInstance', entity: {entityId: 123}})
 */
export function flatObject(obj: KeyValueMap<string, any>): KeyValueMap<string, any> {
  const result = {};
  const path = [];

  function innerFlatObject(anObject: any): void {
    if (anObject !== Object(anObject)) {
      return result[path.join('.')] = anObject;
    }

    for (const key in anObject) {
      if (anObject.hasOwnProperty(key)) {
        path.push(key);
        innerFlatObject(anObject[key]);
        path.pop();
      }
    }
  }

  innerFlatObject(obj);
  return result;
}

export function convertDates(obj: any, paths: string[]): void {
  // TODO: How to handle arrays?
  paths.forEach(path => {
    const value = getByPath(obj, path);
    if (value && !isDate(value)) {
      setByPath(obj, path, new Date(value));
    }
  });
}

export function toArray<T>(item: T | T[]): T[] {
  if (item == null) {
    return null;
  }
  return Array.isArray(item) ? item : [item];
}

export function uniqueByName(data: any[]): any[] {
     return data.filter(
      (item, index, arr) =>
        index === arr.findIndex(test => test.name === item.name)
    );
}

/**
 * Checks whether the app is currently running on the UTP environment,
 * based on the browser's current hostname.
 */
export function isUTPEnvironment(): boolean {
  return window.location.hostname === 'utp.dashboard.saas.temenos.cloud';
}

/* Supports two version numbering schemes: 
 * Date based versions (e.g. "202604") — release if major >= 202604.
 * Simple major-version releases (e.g. "1.x.x") — release if major === 1*/
export function isReleaseVersion(version: string, isUTP: boolean = isUTPEnvironment()): boolean {
  const release = version?.split('.') ?? [];
  const major = Number(release[0]);
  if (major >= 202604) {
    return true;
  }
  return major === 1 && isUTP;
}

export function toTitleCase(text: string): string {
  if (!text || typeof text !== 'string') return '';
  return text.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

export function isSolutionVersion(version: string): boolean {
  const solution = version?.split('.') ?? [];
  return (
    Number(solution[0]) >= 202607
  );
}

/**
 * Replaces all underscores with spaces and applies title case
 * e.g. "failover_inprogress" → "Failover Inprogress"
 */
export function formatAvailabilityState(state: string | undefined): string {
  if (!state) return '';
  return state.replace(/_/g, ' ');
}

export function toUtcString(ts: string | undefined): string | undefined {
  if (!ts) return ts;
  return ts.endsWith('Z') || ts.includes('+') ? ts : ts + 'Z';
}

export function formatDateTime(date: string): string {
  const formatted = new Date(date).toLocaleString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).replace(/,/g, '').trim();
  return formatted.replace(/^(\w+\s+\d{2})\s+/, '$1, ');
}

/** Formats to date format "Oct 15 2026, 12:10:25" (UTC) */
export function formatActivityDateTime(date: string): string {
  const formatted = new Date(date).toLocaleString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).replace(/,/g, '').trim();
  return formatted.replace(/^(\w+\s+\d{2}\s+\d{4})\s+/, '$1, ');
}

export function calculateDuration( startTime: string | undefined,finishTime: string | undefined): string {
  const utcStart = toUtcString(startTime);
  const utcFinish = toUtcString(finishTime);
  if (!utcStart || !utcFinish) return '--';
  const start = new Date(utcStart).getTime();
  const finish = new Date(utcFinish).getTime();
  if (isNaN(start) || isNaN(finish)) return '--';

  const totalSeconds = Math.floor(Math.abs(finish - start) / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
}

// Adjusts menu class based on its position relative to the trigger element (above or below)
export function handleInstanceMenuPositioning(): void {
  setTimeout(() => {
    const overlayPanes = document.querySelectorAll('.cdk-overlay-pane');
    if (overlayPanes.length === 0) return;
    const lastPane = overlayPanes[overlayPanes.length - 1] as HTMLElement;
    const menuPanel = (lastPane.querySelector('mat-mdc-menu-panel') ||
      lastPane.querySelector('.context-menu-dropdown') ||
      lastPane.querySelector('.menu-dropdown')) as HTMLElement;
    if (!menuPanel) {
      return;
    }

    const isMenuAbove = menuPanel.classList.contains('mat-menu-above');
    if (isMenuAbove) {
      menuPanel.classList.add('menu-below');
    } else {
      menuPanel.classList.remove('menu-below');
    }
  }, 0);
}

export function isStartStopRetryAllowed(instance: any): boolean {
  const lastOp = instance?.lastLifecycleOperation;
  return instance?.availabilityState === 'not_ready'
    && ['start', 'stop'].includes(lastOp?.type?.toLowerCase())
    && lastOp?.status?.toLowerCase() === 'failed';
}